import React, { useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { useCombobox, UseComboboxState, UseComboboxStateChangeOptions } from 'downshift';
import { useSelector } from 'react-redux';
import VisuallyHidden from '@reach/visually-hidden';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Client, useSearchClientsQuery } from '../../api/useSearchClientsQuery';
import { Clients } from './components/Clients/Clients';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { ClientsPopover } from './ClientCombobox.styles';
import { InputSizeVariant } from '../../../CommonComponents/OPL/Input/Input.styles';
import { searchInputPlaceholderSelector } from '../../../AdviserArea/AdviserAreaSelector';
import { MESSAGES } from '../../../CommonConstants';
import { DEBOUNCE_TIME } from '../../ClientSearch.meta';
import { currentClientSelector } from '../../../CommonSelectors/CommonAreaSelector';

const stateReducer = (
  state: UseComboboxState<Client>,
  actionAndChanges: UseComboboxStateChangeOptions<Client>,
) => {
  const { type, changes } = actionAndChanges;

  switch (type) {
    case useCombobox.stateChangeTypes.InputBlur:
    case useCombobox.stateChangeTypes.InputKeyDownEscape:
      return {
        ...changes,
        inputValue: '',
      };
    default:
      return changes;
  }
};

interface Props {
  size?: InputSizeVariant;
}

export const ClientCombobox: React.FC<Props> = ({ size }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { isLoading, isError, data: clients } = useSearchClientsQuery(searchTerm);

  const debouncedSetSearchTerm = useRef(
    debounce((term: string) => setSearchTerm(term), DEBOUNCE_TIME),
  ).current;

  useEffectOnce(() => () => {
    debouncedSetSearchTerm.cancel();
  });

  const currentClient = useSelector(currentClientSelector);

  const clientsList = clients?.filter(client => client.ConsumerId !== currentClient?.Id);

  const comboboxBag = useCombobox({
    items: clientsList || [],
    itemToString: client => (client ? `${client.FirstName} ${client.LastName}` : ''),
    onInputValueChange: ({ inputValue }) => {
      debouncedSetSearchTerm(inputValue || '');
    },
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem) {
        ADV.util.Router.go2('ConsumerOverview', { clientId: selectedItem.ConsumerId });
      }
    },
    stateReducer,
  });

  const { isOpen, getComboboxProps, getInputProps, getMenuProps } = comboboxBag;

  const placeholder = useSelector(searchInputPlaceholderSelector);

  return (
    <Box position="relative">
      <Box {...getComboboxProps()}>
        <Input
          {...getInputProps({
            'aria-label': 'Search clients',
            'aria-labelledby': undefined,
          })}
          sizeVariant={size}
          leftIcon="far fa-search"
          placeholder={placeholder}
          formVariant={isError ? 'error' : undefined}
          helperText={isError ? MESSAGES.GENERIC_ERROR : undefined}
        />
      </Box>

      <ClientsPopover
        {...getMenuProps({
          'aria-labelledby': undefined,
        })}
        style={{ display: isOpen && (!!clientsList || isLoading) ? 'block' : 'none' }}
      >
        {isOpen && (
          <Clients isLoading={isLoading} clients={clientsList} comboboxBag={comboboxBag} />
        )}
      </ClientsPopover>
    </Box>
  );
};
